import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fileUrl } from '../../api/client';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';
import PageHeader from '../../components/PageHeader';
import {
  Button, Empty, Loading, Row, Stepper,
} from '../../components/Ui';
import ChargeRows from '../../components/ChargeRows';
import { Reveal } from '../../components/Motion';
import { colors } from '../../theme';
import { currency } from '../../utils/format';

/** One card per shop — KAAMWALA keeps a separate cart for each business. */
const CartPage = () => {
  const navigate = useNavigate();
  const {
    carts, loading, updateItem, clearShop, refresh,
  } = useCart();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const change = async (productId, quantity, lineKey) => {
    setBusy(true);
    try { await updateItem(productId, quantity, lineKey); } catch (err) {
      toast.error(err.message, 'Could not update');
    } finally { setBusy(false); }
  };

  const clear = async (cart) => {
    const ok = await toast.confirm({
      title: 'Clear cart',
      message: `Remove all items from ${cart.partner && cart.partner.businessName}?`,
      confirmLabel: 'Clear',
      danger: true,
    });
    if (ok) clearShop(cart.partner._id);
  };

  if (loading && !carts.length) return <Loading text="Loading your cart…" />;

  return (
    <div>
      <PageHeader
        title="My Cart"
        back={false}
        subtitle={carts.length ? `${carts.length} shop${carts.length > 1 ? 's' : ''}` : 'Nothing added yet'}
        right={carts.length ? (
          <button type="button" className="link-btn" onClick={refresh}>↻ Refresh</button>
        ) : null}
      />

      {!carts.length ? (
        <Empty
          emoji="🛒"
          title="Your cart is empty"
          text="Browse local shops and add fresh groceries, medicines, or anything else you need."
          actionLabel="Start shopping"
          onAction={() => navigate('/home')}
        />
      ) : (
        <div className="grid grid-list">
          {carts.map((cart, ci) => {
            const belowMinimum = cart.partner && cart.partner.minOrderAmount > cart.subtotal;
            return (
              <Reveal key={cart._id} delay={ci * 70} className="card sh-sm" style={{ padding: 18 }}>
                <div className="row" style={{ gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: 18 }}>🏪</span>
                  <div className="grow">
                    <div className="t-title">{cart.partner && cart.partner.businessName}</div>
                    <div className="t-tiny">
                      {cart.partner && cart.partner.deliveryAvailable
                        ? `Delivery ${currency(cart.deliveryCharge)}`
                        : 'Pickup from shop'}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="link-btn"
                    style={{ color: colors.danger }}
                    onClick={() => clear(cart)}
                  >
                    Clear
                  </button>
                </div>

                {cart.items.map((line) => (
                  <div
                    key={line._id || line.lineKey || (line.product && line.product._id)}
                    className="row"
                    style={{ gap: 12, padding: '10px 0', borderTop: `1px solid ${colors.line}` }}
                  >
                    {line.product && line.product.images && line.product.images[0] ? (
                      <img
                        src={fileUrl(line.product.images[0])}
                        alt=""
                        style={{ width: 54, height: 54, borderRadius: 10, objectFit: 'cover', background: colors.bg }}
                      />
                    ) : (
                      <span
                        style={{
                          width: 54, height: 54, borderRadius: 10, background: colors.primaryLight,
                          display: 'grid', placeItems: 'center', fontSize: 20, flex: '0 0 auto',
                        }}
                      >
                        📦
                      </span>
                    )}
                    <div className="grow">
                      <div className="t-body clamp-2">{line.product && line.product.title}</div>
                      {(line.variant && line.variant.label) || (line.addons || []).length || line.note ? (
                        <button
                          type="button"
                          className="link-btn"
                          style={{ display: 'block', textAlign: 'left', padding: 0 }}
                          onClick={() => navigate(`/product/${line.product._id}`, {
                            state: {
                              preset: {
                                lineKey: line.lineKey,
                                variantLabel: (line.variant && line.variant.label) || '',
                                addons: line.addons || [],
                                note: line.note || '',
                                quantity: line.quantity,
                              },
                            },
                          })}
                        >
                          <span className="t-tiny" style={{ display: 'block', color: 'var(--ink2)' }}>
                            {[line.variant && line.variant.label, ...(line.addons || []).map((a) => a.name)].filter(Boolean).join(' · ')}
                            {line.note ? ` · “${line.note}”` : ''}
                          </span>
                          <span className="t-tiny">Customise ›</span>
                        </button>
                      ) : (
                        <div className="t-tiny">{line.product && line.product.unit}</div>
                      )}
                      <div className="t-title" style={{ marginTop: 3 }}>
                        {currency(line.price * line.quantity)}
                      </div>
                    </div>
                    <Stepper
                      value={line.quantity}
                      min={0}
                      disabled={busy}
                      onChange={(q) => change(line.product._id, q, line.lineKey)}
                    />
                  </div>
                ))}

                <div className="divider" />
                <Row label="Item total" value={currency(cart.subtotal)} />
                <Row label="Delivery" value={cart.deliveryCharge ? currency(cart.deliveryCharge) : 'Free'} />
                <ChargeRows doc={cart} />
                <div className="divider" />
                <Row
                  label="To pay"
                  value={currency(cart.totalAmount)}
                  valueStyle={{ fontSize: 16, fontWeight: 800 }}
                />

                {belowMinimum ? (
                  <div
                    style={{
                      background: colors.warningBg, padding: 10, borderRadius: 10, marginTop: 8,
                    }}
                  >
                    <span className="t-small" style={{ color: colors.warning }}>
                      Add {currency(cart.partner.minOrderAmount - cart.subtotal)} more to reach the
                      minimum order.
                    </span>
                  </div>
                ) : null}

                <Button
                  title={`Checkout · ${currency(cart.totalAmount)}`}
                  full
                  disabled={belowMinimum}
                  style={{ marginTop: 12 }}
                  onClick={() => navigate(`/checkout/${cart.partner._id}`, { state: { cart } })}
                />
              </Reveal>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CartPage;
